"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";

import API from "@/utils/api/index"; // axios instance
import ThreadCard from "@/components/ThreadCard";
import FabNewThread from "@/components/ui/FabNewThread";
import Breadcrumbs from "@/components/Breadcrumbs.jsx";
import { buildCommunityBreadcrumbs } from "@/utils/breadcrumbs";

const CATEGORY_OPTIONS = [
  { value: "faq", label: "FAQ" },
  { value: "support", label: "Support" },
  { value: "announcements", label: "Announcements" },
  { value: "fun", label: "Fun" },
  { value: "e-books", label: "E-BOOKS" },
];


const Skeleton = () => (
  <div className="animate-pulse border border-border rounded bg-surface p-4">
    <div className="h-4 w-24 bg-border/60 rounded mb-3" />
    <div className="h-5 w-3/4 bg-border/60 rounded mb-2" />
    <div className="h-4 w-2/3 bg-border/60 rounded" />
  </div>
);

export default function CommunityPage({ category }) {
  const router = useRouter();
  const selectedCategory = (category || "faq").toLowerCase();

  const [typeFilter, setTypeFilter] = useState("all"); // all | discussion | article
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  const crumbs = buildCommunityBreadcrumbs({ category: selectedCategory });

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    const qs = typeFilter === "all" ? "" : `?type=${encodeURIComponent(typeFilter)}`;

    API.get(`/community/category/${encodeURIComponent(selectedCategory)}${qs}`)
      .then((res) => {
        if (!mounted) return;
        setThreads(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => mounted && setThreads([]))
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, [selectedCategory, typeFilter]);

  return (
    <div className="max-w-4xl mx-auto p-4 bg-background min-h-screen text-textPrimary">
      <Breadcrumbs items={crumbs} className="mb-3" />

      <div className="flex items-center gap-3 md:justify-between">
        <label className="text-sm text-textSecondary hidden sm:block">Show:</label>

        <select
          className="border border-border bg-surface text-textPrimary text-sm px-2 py-1 rounded"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="all">All</option>
          <option value="discussion">Discussions</option>
          <option value="article">Articles</option>
        </select>

        <Link
          href="/community/new"
          className="hidden md:inline-flex bg-primary text-white px-4 py-2 rounded hover:bg-accent transition"
        >
          + New Thread
        </Link>
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto snap-x snap-mandatory -mx-4 px-4 no-scrollbar">
        {CATEGORY_OPTIONS.map((c) => (
          <button
            key={c.value}
            type="button"
            onClick={() => router.push(`/community/category/${c.value}`)}
            className={`px-4 py-1.5 text-sm rounded-full transition font-medium border snap-start shrink-0 ${
              c.value === selectedCategory
                ? "bg-primary text-white border-primary"
                : "bg-surface text-textSecondary border-border hover:bg-border"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </div>
      ) : threads.length === 0 ? (
        <p className="text-textSecondary">No threads yet in this category.</p>
      ) : (
        <div className="space-y-4">
          {threads.map((thread) => (
            <ThreadCard key={thread._id} thread={thread} />
          ))}
        </div>
      )}

      <FabNewThread />
    </div>
  );
}
